// Figures of the dashboard: the score ring, severity bars, trends and animated numbers. Plain SVG and
// CSS, no chart library. Every drawing has its numbers written next to it (or in its accessible name),
// so colour is never the only cue, and nothing moves when the person asks for reduced motion.
import {
  CheckCircledIcon,
  CrossCircledIcon,
  ExclamationTriangleIcon,
  InfoCircledIcon,
  QuestionMarkCircledIcon,
} from '@radix-ui/react-icons';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type IssueCounts, type Scores, SEVERITIES, type Severity } from './api';
import type { Tone } from './scan-logic';

export { failuresOf, type Tone, worstTone } from './scan-logic';

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => window.matchMedia(REDUCED_MOTION).matches);
  useEffect(() => {
    const query = window.matchMedia(REDUCED_MOTION);
    const update = () => setReduced(query.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  return reduced;
}

/** A number that counts up to its value, and from one value to the next. */
export function useCountUp(value: number, duration = 900): number {
  const reduced = useReducedMotion();
  const [shown, setShown] = useState(reduced ? value : 0);
  const current = useRef(shown);
  useEffect(() => {
    if (reduced) {
      current.current = value;
      setShown(value);
      return;
    }
    const from = current.current;
    const start = performance.now();
    let frame = 0;
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      current.current = from + (value - from) * (1 - (1 - t) ** 3);
      setShown(current.current);
      if (t < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [value, duration, reduced]);
  return Math.round(shown);
}

/**
 * The counting is for the eyes. Assistive technology always reads the final value (the visually hidden
 * copy), so a screen reader never hears the ticks (53, then 54…) nor a second copy of the same figure.
 */
export function AnimatedNumber({ value, duration }: { value: number; duration?: number }) {
  const { i18n } = useTranslation();
  const shown = useCountUp(value, duration);
  const format = (n: number) => new Intl.NumberFormat(i18n.language).format(n);
  return (
    <span>
      <span className="sr-only">{format(value)}</span>
      <span aria-hidden="true">{format(shown)}</span>
    </span>
  );
}

/** True one frame after mounting: what CSS transitions start from, so drawings grow into place. */
export function useDrawn(): boolean {
  const [drawn, setDrawn] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setDrawn(true));
    return () => cancelAnimationFrame(frame);
  }, []);
  return drawn;
}

const TONE_ICON: Record<Tone, typeof CheckCircledIcon> = {
  critica: CrossCircledIcon,
  grave: ExclamationTriangleIcon,
  moderada: InfoCircledIcon,
  leve: InfoCircledIcon,
  review: QuestionMarkCircledIcon,
  ok: CheckCircledIcon,
  none: InfoCircledIcon,
};

/** Each severity has its own shape as well as its own colour. */
export function ToneIcon({ tone }: { tone: Tone }) {
  const Icon = TONE_ICON[tone];
  return <Icon aria-hidden="true" className="tone-icon" />;
}

/**
 * The accessibility estimate as a ring of 100 points: the automated tests give up to 60 (what they
 * found takes points away) and the manual review the other 40, which stay pending until someone does
 * it. The ring shows the three parts, so a high number never looks like conformance.
 */
export function ScoreRing({
  scores,
  size = 'lg',
}: {
  scores: Scores['accesibilidad'] | null | undefined;
  size?: 'sm' | 'md' | 'lg';
}) {
  const { t } = useTranslation();
  const drawn = useDrawn();
  const score = scores?.score ?? null;
  const automated = scores?.automated ?? (score === null ? null : Math.min(score, 60) / 60);
  const earned = automated === null ? 0 : Math.round(60 * automated);
  const lost = automated === null ? 0 : 60 - earned;
  const manual = Math.round(40 * (scores?.manual ?? 0));
  const pending = 40 - manual;

  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const arc = (points: number, from: number, className: string) => {
    const length = drawn ? (points / 100) * circumference : 0;
    return (
      <circle
        className={`ring-segment ${className}`}
        r={radius}
        cx="60"
        cy="60"
        strokeDasharray={`${length} ${circumference}`}
        strokeDashoffset={-(from / 100) * circumference}
      />
    );
  };

  const label =
    score === null
      ? t('viz.ring.none')
      : size === 'lg'
        ? t('viz.ring.full', { score, earned, lost, pending })
        : t('viz.ring.short', { score });
  return (
    <div className={`score-ring score-ring-${size}`} role="img" aria-label={label}>
      <svg viewBox="0 0 120 120" aria-hidden="true">
        <circle className="ring-track" r={radius} cx="60" cy="60" />
        <g transform="rotate(-90 60 60)">
          {score !== null && (
            <>
              {arc(earned, 0, 'ring-earned')}
              {arc(lost, earned, 'ring-lost')}
              {arc(manual, 60, 'ring-manual')}
              {arc(pending, 60 + manual, 'ring-pending')}
            </>
          )}
        </g>
      </svg>
      <span className="score-ring-value" aria-hidden="true">
        {score === null ? '—' : <CountUpText value={score} />}
        {score !== null && size !== 'sm' && <small>/100</small>}
      </span>
    </div>
  );
}

function CountUpText({ value }: { value: number }) {
  return <>{useCountUp(value)}</>;
}

/** What each part of the ring means, with its points. */
export function ScoreLegend({ scores }: { scores: Scores['accesibilidad'] | null | undefined }) {
  const { t } = useTranslation();
  if (scores?.score == null) return null;
  const automated = scores.automated ?? Math.min(scores.score, 60) / 60;
  const earned = Math.round(60 * automated);
  const manual = Math.round(40 * (scores.manual ?? 0));
  const items = [
    { key: 'earned', points: earned },
    { key: 'lost', points: 60 - earned },
    { key: 'manual', points: manual },
    { key: 'pending', points: 40 - manual },
  ].filter((item) => item.points > 0 || item.key === 'earned' || item.key === 'pending');
  const pending = 40 - manual;
  return (
    <>
      <ul className="ring-legend">
        {items.map((item) => (
          <li key={item.key} data-part={item.key}>
            <span className="ring-swatch" aria-hidden="true" />
            <span>{t(`viz.ring.parts.${item.key}`)}</span>
            <strong>{t('viz.points', { count: item.points })}</strong>
          </li>
        ))}
      </ul>
      {pending > 0 && (
        <p className="ring-legend-hint">{t('viz.ring.pendingHint', { pending, max: 100 - pending })}</p>
      )}
    </>
  );
}

/** Failures by severity as one bar, with the counts written below it. */
export function SeverityBar({
  counts,
  compact = false,
}: {
  counts: Record<Severity, number>;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const drawn = useDrawn();
  const total = SEVERITIES.reduce((sum, severity) => sum + counts[severity], 0);
  return (
    <div className={`severity-bar ${compact ? 'severity-bar-compact' : ''}`}>
      <div className="severity-track" aria-hidden="true" data-empty={total === 0 || undefined}>
        {total > 0 &&
          SEVERITIES.filter((severity) => counts[severity] > 0).map((severity) => (
            <span
              key={severity}
              className="severity-fill"
              data-tone={severity}
              style={{ width: drawn ? `${(counts[severity] / total) * 100}%` : '0%' }}
            />
          ))}
      </div>
      {!compact && (
        <ul className="severity-legend">
          {SEVERITIES.map((severity) => (
            <li key={severity} data-tone={severity}>
              <ToneIcon tone={severity} />
              <span>{t(`viz.severities.${severity}`)}</span>
              <strong>
                <AnimatedNumber value={counts[severity]} />
              </strong>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** A page's or a scan's counts in a few chips: failures by severity, or a check when there are none. */
export function SeverityChips({ counts }: { counts: IssueCounts | null | undefined }) {
  const { t } = useTranslation();
  if (!counts) return null;
  const present = SEVERITIES.filter((severity) => counts.accessibility.violations[severity] > 0);
  if (present.length === 0) {
    return (
      <span className="tone-chip" data-tone={counts.accessibility.review > 0 ? 'review' : 'ok'}>
        <ToneIcon tone={counts.accessibility.review > 0 ? 'review' : 'ok'} />
        {counts.accessibility.review > 0
          ? t('viz.chips.review', { count: counts.accessibility.review })
          : t('viz.chips.ok')}
      </span>
    );
  }
  return (
    <span className="tone-chips">
      {present.map((severity) => (
        <span key={severity} className="tone-chip" data-tone={severity}>
          <ToneIcon tone={severity} />
          <span aria-hidden="true">{counts.accessibility.violations[severity]}</span>
          <span className="sr-only">
            {t(`viz.chips.${severity}`, { count: counts.accessibility.violations[severity] })}
          </span>
        </span>
      ))}
    </span>
  );
}

/** Horizontal bars with their label and value written out (top issues, WCAG principles…). */
export function BarList({
  items,
  label,
}: {
  items: { key: string; label: ReactNode; value: number; tone?: Tone }[];
  label: string;
}) {
  const drawn = useDrawn();
  const max = Math.max(1, ...items.map((item) => item.value));
  return (
    <ul className="bar-list" aria-label={label}>
      {items.map((item) => (
        <li key={item.key}>
          <span className="bar-label">{item.label}</span>
          <span className="bar-track" aria-hidden="true">
            <span
              className="bar-fill"
              data-tone={item.tone}
              style={{ width: drawn ? `${(item.value / max) * 100}%` : '0%' }}
            />
          </span>
          <strong className="bar-value">{item.value}</strong>
        </li>
      ))}
    </ul>
  );
}

/** A small line of the latest scores, for a card. */
export function Sparkline({ values }: { values: number[] }) {
  if (values.length < 2) return null;
  const width = 96;
  const height = 28;
  const x = (i: number) => (i / (values.length - 1)) * width;
  const y = (v: number) => height - 2 - (v / 100) * (height - 4);
  const points = values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  return (
    <svg className="sparkline" viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <polyline points={points} />
      <circle cx={x(values.length - 1)} cy={y(values.at(-1)!)} r="2.5" />
    </svg>
  );
}

/**
 * The score of each finished scan over time. The chart is a picture (its accessible name says where it
 * starts and ends); the history list next to it has the links to each scan. The drawing is SVG and the
 * labels are HTML on a solid background, so their contrast can be checked.
 */
export function TrendChart({
  points,
  current,
  onSelect,
}: {
  points: { id: string; date: string; score: number | null }[];
  current?: string;
  onSelect?: (id: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const drawn = useDrawn();
  const scored = points.filter(
    (point): point is { id: string; date: string; score: number } => point.score !== null,
  );
  if (scored.length < 2) return null;
  const width = 640;
  const height = 180;
  const pad = { top: 26, right: 24, bottom: 30, left: 24 };
  const x = (i: number) => pad.left + (i / (scored.length - 1)) * (width - pad.left - pad.right);
  const y = (score: number) => pad.top + (1 - score / 100) * (height - pad.top - pad.bottom);
  const line = scored
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.score).toFixed(1)}`)
    .join(' ');
  const area = `${line} L${x(scored.length - 1).toFixed(1)},${y(0)} L${x(0).toFixed(1)},${y(0)} Z`;
  const day = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'short' }).format(new Date(iso));
  // Several scans on one day: the next ones show their time.
  const when = (i: number) => {
    const date = scored[i]!.date;
    const previous = scored[i - 1]?.date;
    return previous && day(previous) === day(date)
      ? new Intl.DateTimeFormat(i18n.language, { timeStyle: 'short' }).format(new Date(date))
      : day(date);
  };
  const first = scored[0]!;
  const last = scored.at(-1)!;
  // With many scans only some dates fit under the line.
  const every = Math.ceil(scored.length / 6);
  const percent = (value: number, total: number) => `${(value / total) * 100}%`;
  return (
    <div
      className="trend-chart"
      role="img"
      aria-label={t('viz.trend.label', {
        count: scored.length,
        from: first.score,
        to: last.score,
        start: day(first.date),
        end: day(last.date),
      })}
      data-drawn={drawn || undefined}
    >
      <svg viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
        {[0, 50, 100].map((value) => (
          <line
            key={value}
            className="trend-grid"
            x1={pad.left}
            x2={width - pad.right}
            y1={y(value)}
            y2={y(value)}
          />
        ))}
        <path className="trend-area" d={area} />
        <path className="trend-line" d={line} pathLength={1} />
        {scored.map((p, i) => (
          // A pointer shortcut to each scan; keyboards have the history list.
          // biome-ignore lint/a11y/noStaticElementInteractions: the history list has the same links.
          <circle
            key={p.id}
            className="trend-point"
            data-current={p.id === current || undefined}
            cx={x(i)}
            cy={y(p.score)}
            r={p.id === current ? 6 : 4.5}
            onClick={onSelect ? () => onSelect(p.id) : undefined}
          />
        ))}
      </svg>
      {scored.map((p, i) => (
        <span
          key={p.id}
          className="chart-score"
          aria-hidden="true"
          style={{ left: percent(x(i), width), top: percent(y(p.score) - 9, height) }}
        >
          {p.score}
        </span>
      ))}
      {scored.map((p, i) =>
        i % every === 0 || i === scored.length - 1 ? (
          <span
            key={`${p.id}-date`}
            className="chart-date"
            aria-hidden="true"
            style={{ left: percent(x(i), width), top: percent(height - pad.bottom + 8, height) }}
          >
            {when(i)}
          </span>
        ) : null,
      )}
    </div>
  );
}
